import {
  AdhocKycDvsFailedField,
  AdhocKycPriorAttempt,
  AdhocKycPriorDetails,
  AdhocKycRetryOffer,
} from "src/store/features/api/adhocKyc";

/**
 * In-flow retry copy. When the DVS check says no-match, the final screen
 * names the element(s) the document issuer rejected and offers "Check and
 * resubmit" in the same session. The re-opened row carries `priorAttempt`,
 * so the details step skips straight to the form, which repeats the message,
 * prefills what the customer typed last time and flags the fields to check.
 * Field keys are the backend's `AdhocKycDvsFailedField` vocabulary.
 */
export const FAILED_FIELD_LABELS: Record<AdhocKycDvsFailedField, string> = {
  given_names: "given name",
  family_name: "family name",
  date_of_birth: "date of birth",
  card_number: "licence card number",
  document_number: "document number",
};

const labelOf = (field: string): string =>
  FAILED_FIELD_LABELS[field as AdhocKycDvsFailedField] || field.replace(/_/g, " ");

/** "your given name" / "your given name and licence card number" — or "your details" when the issuer gave no element detail. */
export const failedFieldsPhrase = (fields: string[] | undefined): string => {
  const labels = (fields || []).map(labelOf);
  if (!labels.length) return "your details";
  if (labels.length === 1) return `your ${labels[0]}`;
  return `your ${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
};

/** The sentence compliance agreed on (2026-10-23): "The issuer could not match your given name with their records." */
export const issuerMismatchSentence = (fields: string[] | undefined): string =>
  `The issuer could not match ${failedFieldsPhrase(fields)} with their records.`;

/** What to do about it, on the final screen. */
export const retryInstruction = (offer: AdhocKycRetryOffer): string => {
  const it = offer.failedFields.length === 1 ? "it" : "them";
  const check = `Please check ${it} against your driver\u2019s licence or passport and resubmit.`;
  return offer.reusesIdCheck
    ? `${check} You won\u2019t need to repeat the ID check.`
    : `${check} Because your details are changing, we\u2019ll ask you to complete a quick ID check afterwards.`;
};

/** The sentence that opens the retry form: what went wrong last time and what to do about it. */
export const priorAttemptMessage = (
  prior: AdhocKycPriorAttempt | undefined,
): string | undefined => {
  if (!prior) return undefined;
  if (prior.dvsStatus === "no_match") {
    const it = prior.failedFields.length === 1 ? "it" : "them";
    return `${issuerMismatchSentence(prior.failedFields)} Please type ${
      prior.failedFields.length ? it : "your details"
    } exactly as printed on your ID.`;
  }
  if (prior.dvsStatus === "error") {
    return "Your last attempt could not be checked. Please go through the steps again.";
  }
  return "Please go through the steps again.";
};

/** Which form inputs each rejected field maps to, so the form can flag them. */
export const FAILED_FIELD_FORM_FIELDS: Record<AdhocKycDvsFailedField, string[]> = {
  given_names: ["firstName", "middleName"],
  family_name: ["lastName"],
  date_of_birth: ["dateOfBirth"],
  card_number: ["doc:driversLicenceCardNo"],
  document_number: ["doc:driversLicenceNo", "doc:passportNo", "doc:driversLicenceVersion"],
};

export const flaggedFormFields = (prior: AdhocKycPriorAttempt | undefined): Set<string> =>
  new Set(
    (prior?.failedFields || []).flatMap(
      (f) => FAILED_FIELD_FORM_FIELDS[f as AdhocKycDvsFailedField] || [],
    ),
  );

export const FLAGGED_FIELD_HINT =
  "The issuer did not match this last time \u2014 check it against your ID";

/** "1988-03-14" / "1988-03-14T00:00:00.000Z" → "14/03/1988"; anything else → "". */
export const isoToDateOfBirthInput = (iso: string | undefined): string => {
  const match = (iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
};

/** The form values the prior attempt's typed details prefill (only keys with a value). */
export const priorDetailsValues = (
  details: AdhocKycPriorDetails | undefined,
): Record<string, string> => {
  if (!details) return {};
  const values: Record<string, string> = {};
  const put = (key: string, value: string | undefined): void => {
    if (value) values[key] = value;
  };
  put("firstName", details.firstName);
  put("middleName", details.middleName);
  put("lastName", details.lastName);
  put("dateOfBirth", isoToDateOfBirthInput(details.dateOfBirth));
  put("phone", details.phone);
  put("streetAddress", details.address?.streetAddress);
  put("city", details.address?.city);
  put("state", details.address?.state);
  put("postcode", details.address?.postcode);
  put("country", details.address?.country);
  put("primaryCitizenship", details.primaryCitizenship);
  put("secondaryCitizenship", details.secondaryCitizenship);
  return values;
};
